import { useState, useMemo } from "react"
import { Search, ExternalLink, Copy, Check, Trash2 } from "lucide-react"
import { getUserColor } from "../lib/utils"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { Badge } from "../components/ui/badge"
import { useLocale } from "../components/LocaleProvider"
import { toast } from "../components/ui/toast"
import { useArchivedRecords, useClients, useDeleteDashboardRecord } from "../lib/queries"
import type { DashboardRecord, Client } from "../types"
import type { TranslationKey } from "../i18n/translations"

function extractExposeId(url: string): string | null {
  if (!url) return null
  const expose = url.match(/\/expose\/(\d+)/)
  if (expose) return expose[1]
  // Fall back to the last numeric path segment (e.g. .../appointments/3745525064)
  const segments = Array.from(url.matchAll(/\/\d{3,}(?=[/?#]|$)/g))
  const last = segments.pop()
  return last ? last[0].slice(1) : null
}

const STATUS_T_KEY: Record<string, TranslationKey> = {
  "Request submitted": "status_request_submitted",
  "Termin invitation": "status_termin_invitation",
  Waiting: "status_waiting",
  Completed: "status_completed",
  "No relevant": "status_no_relevant",
  Denied: "status_denied",
  Absage: "status_absage",
  Error: "status_error",
}

function statusVariant(status: string): "success" | "info" | "warning" | "danger" | "neutral" {
  if (status === "Completed") return "success"
  if (status === "Request submitted") return "info"
  if (status === "Termin invitation") return "info"
  if (status === "Waiting") return "warning"
  if (status === "Denied") return "danger"
  if (status === "Absage") return "warning"
  if (status === "Error") return "danger"
  return "neutral"
}

const MAIL_LABEL: Record<string, string> = {
  M2: "G2",
  M3: "M3",
  M4: "F4",
  M5: "R5",
  M6: "T6",
}

function Archive() {
  const { data: archiveData, isLoading } = useArchivedRecords()
  const archiveRecords: DashboardRecord[] = archiveData ?? []
  const { data: clientsData } = useClients()
  const clients: Client[] = clientsData ?? []
  const { t } = useLocale()
  const [search, setSearch] = useState("")
  const [clientFilter, setClientFilter] = useState("")
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const deleteMut = useDeleteDashboardRecord()

  const clientColorMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.fullName, c.color || getUserColor(c.fullName)])),
    [clients],
  )

  const uniqueClients = useMemo(
    () => Array.from(new Set(archiveRecords.map((r) => r.client))).sort(),
    [archiveRecords],
  )

  const filtered = useMemo(
    () =>
      archiveRecords.filter((row) => {
        if (clientFilter && row.client !== clientFilter) return false
        return Object.values(row).some((v) => {
          if (typeof v === "object" && v !== null) {
            return Object.entries(v)
              .filter(([, val]) => val)
              .map(([key]) => key)
              .join(", ")
              .toLowerCase()
              .includes(search.toLowerCase())
          }
          return typeof v === "string" && v.toLowerCase().includes(search.toLowerCase())
        })
      }),
    [archiveRecords, search, clientFilter],
  )

  const handleCopy = async (url: string, index: number) => {
    await navigator.clipboard.writeText(url)
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 1500)
    toast(t("toast_copied"))
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search_archive")}
              className="h-10 w-full max-w-sm rounded-md border border-input bg-background pl-10 pr-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">{t("filter_all_clients")}</option>
            {uniqueClients.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("th_date")}</TableHead>
              <TableHead>{t("th_time")}</TableHead>
              <TableHead>{t("th_client")}</TableHead>
              <TableHead>{t("th_account")}</TableHead>
              <TableHead>{t("th_strategy")}</TableHead>
              <TableHead>{t("th_origin")}</TableHead>
              <TableHead>{t("th_object_id")}</TableHead>
              <TableHead>{t("th_object_link")}</TableHead>
              <TableHead>{t("th_status")}</TableHead>
              <TableHead>{t("th_termin_date")}</TableHead>
              <TableHead>{t("th_archived_at")}</TableHead>
              <TableHead>{t("th_archived_by")}</TableHead>
              <TableHead>{t("th_mails")}</TableHead>
              <TableHead>{t("th_comment")}</TableHead>
              <TableHead className="w-16">{t("th_actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={15} className="text-center text-muted-foreground py-8">
                  {t("empty_no_results")}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{row.date}</TableCell>
                  <TableCell>{row.time}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                        style={{
                          backgroundColor: clientColorMap[row.client] || getUserColor(row.client),
                        }}
                      />
                      {row.client}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.account}</TableCell>
                  <TableCell>{row.strategy}</TableCell>
                  <TableCell>
                    <span className="text-xs">{row.origin || "—"}</span>
                  </TableCell>
                  <TableCell className="font-mono text-xs whitespace-nowrap">
                    {row.objectId}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs">
                        {extractExposeId(row.objectLink) || row.objectLink}
                      </span>
                      <button
                        onClick={() => handleCopy(row.objectLink, i)}
                        className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center transition-colors"
                        title={row.objectLink}
                      >
                        {copiedIndex === i ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </button>
                      <a
                        href={row.objectLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center transition-colors"
                        title={t("tip_open_link")}
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Badge variant={statusVariant(row.status)} className="uppercase">
                      {t(STATUS_T_KEY[row.status])}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {row.terminDate ? (
                      <span>
                        {new Date(row.terminDate).toLocaleDateString("de-DE", {
                          day: "2-digit",
                          month: "2-digit",
                        })}{" "}
                        {new Date(row.terminDate).toLocaleTimeString("de-DE", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {row.archivedAt ? (
                      <span>
                        {new Date(row.archivedAt).toLocaleDateString("de-DE", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}{" "}
                        {new Date(row.archivedAt).toLocaleTimeString("de-DE", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{row.archivedBy || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {(["M2", "M3", "M4", "M5", "M6"] as const).map((m) => (
                        <span
                          key={m}
                          className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium ${
                            row.mails[m]
                              ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                              : "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500"
                          }`}
                        >
                          {MAIL_LABEL[m]}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className="text-xs text-muted-foreground max-w-[160px] inline-block truncate"
                      title={row.comment || ""}
                    >
                      {row.comment || "—"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => row.id && deleteMut.mutate(row.id)}
                      className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors text-destructive"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default Archive
